import { Link } from "react-router";

export const trainingFAQs = [
  {
    question: "What is MakerGhat's Teacher Training program?",
    answer:
      "MakerGhat’s teacher training equips educators with the skills and mindset to facilitate hands-on, project-based learning. Teachers learn to run makerspaces, integrate STEM/STEAM into classrooms, and nurture 21st-century skills like critical thinking, collaboration, and creativity.",
  },
  {
    question:
      "Why is teacher training important for scaling the maker movement?",
    answer:
      "Tools alone don’t transform classrooms — teachers do. Training ensures educators can adapt curriculum, guide student-led projects, and create safe, inclusive spaces. This shift reduces rote learning, improves learning outcomes, and builds employability skills for youth in rural and urban India.",
  },
  {
    question: "What skills do teachers gain from this training?",
    answer:
      "Teachers gain hands-on technical proficiency with maker tools, plus strategies to foster problem-solving, self-efficacy, communication, and collaboration. They also learn design thinking and curriculum integration, empowering them to guide students in building real-world solutions through maker projects.",
  },
  {
    question: "How long does MakerGhat's training program take?",
    answer:
      "The training is flexible and modular. Educators undergo intensive live workshops and receive continuous support through our AI-powered platform, MakerDost. This blended model ensures teachers can immediately apply what they learn in their classrooms across India.",
  },
  {
    question: "How does teacher training improve student outcomes?",
    answer:
      "A single trained teacher impacts 60-100 students annually by fostering curiosity, 21st-century skills, and entrepreneurial abilities. Over time, this reduces dropout rates, improves employability, and creates problem-solvers who positively impact their families and communities.",
  },
  {
    question: "How much does MakerGhat's teacher training cost?",
    answer:
      "It costs approximately ₹1,000 per teacher per year. This small investment has a multiplier effect, as one teacher reaches at least 100 students each year, creating long-term impact across 1,000+ members of communities in India’s aspirational and underserved districts.",
  },
  {
    question: "Does this model scale?",
    answer:
      "Yes. Our training is affordable, scalable, and syllabus-integrated, designed for any school or college in India. With localized curriculum in vernacular languages and maker kits at all price points, we adapt easily across urban, rural, and peri-urban classrooms nationwide.",
  },
  {
    question: "How can I support MakerGhat's training initiative?",
    jsxAnswer: true,
    answer: (
      <>
        You can partner, volunteer, or donate to scale teacher training across
        India. Supporting one teacher enables hundreds of students to gain
        skills for the future. Click on our{" "}
        <Link
          to="/get-involved/partner"
          style={{ textDecoration: "underline" }}
        >
          Get involved
        </Link>{" "}
        page to make an impact.
      </>
    ),
  },
];

export const evidenceFrameworkFAQs = [
  {
    question: "What is MakerGhat's approach to M&E?",
    answer:
      "At MakerGhat, M&E is a core part of learning and accountability. We use participatory, adaptive, and evidence-driven methods to track how our programs in India’s schools improve 21st-century skills, employability, and entrepreneurial learning outcomes.",
  },
  {
    question: "What indicators do you track through M&E?",
    answer:
      "We track both program-level and participant-level indicators. These include session quality, teacher adoption, attendance, and academic progress, as well as growth in 21st-century skills: critical thinking, problem-solving, communication, collaboration, creativity, and self-efficacy.",
  },
  {
    question: "How do you measure your programs' success?",
    answer:
      "Success is measured by how effectively we deliver quality maker education and how much students grow in skills, confidence, and employability. Data from MakerDost and field observations is triangulated to track outcomes across schools in India.",
  },
  {
    question: "How does your Theory of Change guide M&E?",
    answer:
      "Our Theory of Change maps the journey from tools and teacher training to empowered youth. M&E validates each step, ensuring that makerspaces, curriculum, and teacher training translate into measurable student outcomes and community-level change.",
  },
  {
    question: "How does MakerDost help in monitoring your programs?",
    answer:
      "MakerDost, our AI-powered platform, enables continuous monitoring by collecting classroom data directly from teachers and students. It tracks learning outcomes, attendance, and skill development, giving us insights into program quality across public schools in India.<br/><br/>MakerDost makes data collection fast, inclusive, and real-time. Teachers and students submit responses through WhatsApp in multiple Indian languages.This ensures last-mile schools — even in aspirational districts — are part of transparent, reliable monitoring.",
  },
  {
    question: "What makes MakerDost unique?",
    answer:
      "Unlike traditional surveys, MakerDost is low-cost, vernacular-friendly, and mobile-first, designed for public schools in India. It works in low-bandwidth areas, ensures real-time dashboards, and integrates feedback from communities — making M&E accessible and transparent. <a class='underline' target='_blank' href='/get-involved/partner'>Click here</a>",
  },
];

export const evidenceDashboardFAQs = [
  {
    question: "How can I use MakerGhat's interactive dashboard?",
    answer:
      "Our real-time education dashboard lets you click on states and explore live data. You can see reach, student skill development, and program outcomes across rural, peri-urban, and urban schools in India.",
  },
  {
    question: "Who uses MakerGhat's data?",
    answer:
      "Teachers use it to refine their classrooms, partners track program reach, and funders measure ROI. By making M&E interactive and open, stakeholders across India gain real-time insights into education outcomes.",
  },
];

export const evidenceReportsFAQs = [
  {
    question: "Who uses MakerGhat reports?",
    answer:
      "Our reports are used by educators, policymakers, researchers, CSR partners, and communities across India. By making them open-source, we ensure anyone — from a rural teacher to an international donor — can access, adapt, and learn from our impact.",
  },
  {
    question: "Are reports open source?",
    answer:
      "Yes. All MakerGhat reports, case studies, and insights — including annual reports, program reports, and case studies from schools in India — are shared under the Creative Commons Attribution-ShareAlike 4.0 (CC BY-SA 4.0) License, making them fully open-source and freely accessible worldwide.",
  },
  {
    question: "How can I download MakerGhat reports?",
    answer:
      "To download our reports, simply fill in your details on the form on the page that appears. Once submitted, your document will automatically be downloaded.",
  },
  {
    question: "How can I cite MakerGhat reports?",
    answer:
      "To cite our reports, please use the format: MakerGhat, [Report Title], [Year] Example:MakerGhat, Annual Report, 2025. This ensures clear attribution under the CC BY-SA 4.0 license.",
  },
];

export const curriculumFAQs = [
  {
    question:
      "Why does MakerGhat share their customised curriculum as open resources?",
    answer:
      "At MakerGhat, we believe learning should be accessible to all. By sharing activities openly, we empower teachers, schools, and organizations worldwide with hands-on, real-world learning tools — free of cost. Open access helps us build a global community of makers and learners who collaborate and grow together.",
  },
  {
    question: "How can I download MakerGhat's open sources and reports?",
    answer:
      "To download our free activities, simply fill in your details on the form on the page that appears. Once submitted, your document will automatically download.",
  },
  {
    question: "How can I use the activities once I download them?",
    answer:
      "You are free to use, adapt, and share MakerGhat’s activities under the Creative Commons Attribution-ShareAlike 4.0 (CC BY-SA 4.0) license. This means you can use them in any programs, or workshops — as long as you credit MakerGhat while sharing or building upon any adaptations under the same license.",
  },
  {
    question: "Can I adapt or modify any of the activities I download?",
    answer:
      "Absolutely. MakerGhat’s open resources are designed to be flexible. You can remix, adapt, or expand on the activities to fit your teaching context. Just remember: any modifications you share must be credited.",
  },
  {
    question:
      "Can I collaborate with MakerGhat to co-create or scale these activities?",
    answer:
      "Yes! MakerGhat actively partners with schools, NGOs, and organizations to co-develop and scale innovative learning activities. If you’d like to collaborate, reach out to us via our \"Partner\" page under Get Involved tab (found in the main menu), or <a class='underline' target='_blank' href='/get-involved/partner'>Click here</a>",
  },
  {
    question: "Can I embed MakerGhat curriculum and activities on my website?",
    answer:
      "Yes. Our resources can be embedded on external websites, provided attribution is given to MakerGhat under the CC BY-SA 4.0 license. This helps extend access to education impact data across India.",
  },
];

export const makerspaceFAQs = [
  {
    question: "What is a makerspace?",
    answer:
      "A makerspace is a hands-on learning environment where students explore science, technology, engineering, and design through making, experimentation, and real-world problem-solving.",
  },
  {
    question: "How is a MakerGhat makerspace different from a traditional lab?",
    answer:
      "Unlike traditional labs that follow fixed experiments, MakerGhat makerspaces focus on exploration, iteration, and creativity. Students are encouraged to build, fail, test, and refine ideas rather than follow rote instructions.",
  },
  {
    question: "Who does MakerGhat design makerspaces for?",
    answer:
      "MakerGhat designs makerspaces for students, educators, schools, colleges, NGOs, and community organisations across urban, rural, and aspirational districts in India seeking experiential, maker-centered learning.",
  },
  {
    question:
      "Are MakerGhat makerspaces suitable for rural and low-resource settings?",
    answer:
      "Yes. MakerGhat makerspaces are intentionally low-cost, scalable, and adaptable. They are designed to function effectively in rural schools, government institutions, and low-resource settings across India.",
  },
  {
    question:
      "Can a school, college, or community set up a MakerGhat makerspace?",
    answer:
      "Yes. Schools, educational institutions, NGOs, and community spaces can partner with MakerGhat to set up a customised makerspace aligned with their local context and learner needs.",
  },
  {
    question: "What is the process to set up a makerspace?",
    jsxAnswer: true,
    answer: (
      <>
        The process includes understanding your context, designing a suitable
        makerspace model, setting up tools and labs, and training educators or
        facilitators. Click here to enquire for a makerspace setup{" "}
        <Link
          to="/get-involved/partner"
          style={{ textDecoration: "underline" }}
        >
          click here
        </Link>
        .
      </>
    ),
  },
  {
    question: "Do you train teachers or facilitators to run the makerspace?",
    answer:
      "Yes. MakerGhat provides educator and facilitator training to ensure makerspaces are used effectively and sustainably, enabling long-term impact beyond the initial setup.",
  },
];

export const makerkitFAQs = [
  {
    question: "Do schools need a makerspace or lab to use Makerkits?",
    answer:
      "No. Makerkits are designed to work even in regular classrooms without labs, making hands-on STEM learning accessible anywhere.",
  },
  {
    question: "Can Makerkits be used in any school?",
    answer:
      "Yes. Makerkits are specifically designed for rural, low-resource, and last-mile school contexts across India.",
  },
  {
    question:
      "Are Makerkits aligned with curriculum or experiential learning frameworks?",
    answer:
      "Yes. Makerkits support experiential and project-based learning and can be aligned with school curricula and skill development programs.",
  },
  {
    question: "Can NGOs or organisations use Makerkits for community programs?",
    jsxAnswer: true,
    answer: (
      <>
        Absolutely. Makerkits are widely used in schools, NGOs, and community
        learning programs. Organisations can connect with MakerGhat to discuss
        quantities, customisation, and implementation. Click on our{" "}
        <Link
          to="/get-involved/partner"
          style={{ textDecoration: "underline" }}
        >
          partner
        </Link>{" "}
        page to make an impact.
      </>
    ),
  },
  {
    question: "Does MakerGhat provide training to use Makerkits effectively?",
    answer:
      "Yes. MakerGhat offers educator and facilitator training to ensure Makerkits are used meaningfully and sustainably.",
  },
  {
    question: "What age groups are Makerkits suitable for?",
    answer:
      "Makerkits are adaptable for primary, middle, and secondary school students, as well as youth programs, depending on curriculum design and facilitation.",
  },
  {
    question: "What skills do students learn using Makerkits?",
    answer:
      "Students develop 21st-century skills such as problem-solving, creativity, collaboration, and critical thinking through hands-on, experiential STEM learning.",
  },
];

export const mgFellowShipFAQs1 = [
  {
    question: "What is the MakerGhat Fellowship?",
    answer:
      "The MakerGhat Fellowship is a two-year leadership program by MakerGhat that equips young professionals to drive grassroots change in education, innovation, and community ecosystems across rural, peri-urban, and urban districts of India.",
  },
  {
    question: "Who can apply for the MakerGhat Fellowship?",
    answer:
      "Graduates from any discipline who are passionate about education, innovation, and community development in rural, peri-urban, and urban districts of India are encouraged to apply.",
  },
  {
    question: "Do I need prior experience in rural development to apply?",
    answer:
      "No prior experience is required. The fellowship provides structured training, mentorship, and hands-on exposure to help fellows learn while working closely with communities across diverse districts. However, having prior experience is always a bonus",
  },
  {
    question: "What kind of training does the fellowship provide?",
    answer:
      "The fellowship offers training in leadership, teaching and pedagogy, technical skills and tinkering, monitoring and evaluation, community engagement, and innovation ecosystem building.",
  },
  {
    question: "What will fellows do during the MakerGhat Fellowship?",
    answer:
      "Fellows work with schools, teachers, and communities to implement experiential learning, makerspaces, and innovation programs while strengthening local education and leadership ecosystems.",
  },
  {
    question: "Where will fellows be placed during the program?",
    answer:
      "Fellows are placed in rural, peri-urban, and urban districts across India, where they engage deeply with local schools, educators, and community stakeholders.",
  },
  {
    question: "Is there financial support for fellows?",
    answer:
      "Yes. The fellowship provides a monthly stipend of INR 25,000 and resources that allow fellows to focus fully on their work and community engagement.",
  },
  {
    question: "What skills will fellows gain during the fellowship?",
    answer:
      "Fellows develop leadership, facilitation, teaching, innovation, problem-solving, community engagement, and impact measurement skills through real-world field experience.",
  },
  {
    question: "Is relocation required for the MakerGhat Fellowship?",
    answer:
      "Yes. Fellows are expected to live and work in their assigned districts to build strong relationships with schools, educators, and communities.",
  },
  {
    question:
      "When do MakerGhat Fellowship applications open and how can I apply for it?",
    jsxAnswer: true,
    answer: (
      <>
        Applications typically open once a year. Visit the{" "}
        <a href="#apply" style={{ textDecoration: "underline" }}>
          application section
        </a>{" "}
        on this page to check the latest details. You can apply through the
        MakerGhat website by completing the{" "}
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLScCHD_MN5doivKO0wMjuoC1wgWT4uWZrzv-243hwN5WlIlQ3Q/viewform"
          style={{ textDecoration: "underline" }}
          target="_blank"
          rel="noreferrer"
        >
          online application form
        </a>{" "}
        and participating in the fellowship selection process.
      </>
    ),
  },
];

export const igniteFAQs = [
  {
    question: "What is the IGNITE Incubator Program?",
    answer:
      "The IGNITE Incubator Program supports students and young innovators across India to develop ideas into real-world solutions through mentorship, hands-on learning, and structured incubation.",
  },
  {
    question: "Who can apply for the IGNITE Incubator Program in India?",
    answer:
      "Students, youth, and early-stage innovators from rural, peri-urban, and urban districts of India with innovative ideas or problem-solving interest can apply.",
  },
  {
    question: "Do I need a fully developed idea to apply?",
    answer:
      "No. Applicants can apply with early-stage ideas, problem statements, or even strong curiosity to explore innovation and build solutions.",
  },
  {
    question: "What support does the IGNITE Incubator provide?",
    answer:
      "Participants receive mentorship, access to MakerGhat tools and makerspaces, structured training, and guidance to build, test, and refine their ideas.",
  },
  {
    question: "How long is the IGNITE Incubator Program?",
    answer:
      "The duration varies by cohort, but typically includes structured phases of ideation, prototyping, and showcasing over six months.",
  },
  {
    question: "How can schools or institutions partner with IGNITE?",
    answer:
      "Schools, colleges, and organisations can collaborate with MakerGhat to implement the IGNITE Incubator by visiting the partnerships page and applying.",
  },
  {
    question: "How do I apply for the IGNITE Incubator Program?",
    jsxAnswer: true,
    answer: (
      <>
        You can apply through the official MakerGhat website by filling out the
        form here:{" "}
        <a
          href="/programs/ignite-incubator#contactUsForm"
          style={{ textDecoration: "underline" }}
        >
          IGNITE Incubator Application
        </a>
        {". "}
        We will personally reach out to you when the cohort begins.
      </>
    ),
  },
];
